import { CircularLoading, generateBase64, useToast } from '@foxtaibox/foxtailbox-frontend-core';
import { useLoginByTelegramTokenMutation } from '@generated/graphql';
import { Box } from '@mui/material';
import useHandleLogin from 'pages/onboarding/Login/hooks/useHandleLogin';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useMe from 'store/user/useMe';

const BOT_NAME = process.env.REACT_APP_BOT_NAME;

interface TelegramLoginWidgetProps {
  setLoginLoading: Dispatch<SetStateAction<boolean>>;
}

const TelegramLoginWidget = ({ setLoginLoading }: TelegramLoginWidgetProps) => {
  const { me, loading: meLoading } = useMe();
  const { t } = useTranslation();
  const handleLogin = useHandleLogin();
  const telegramWrapperRef = useRef<HTMLDivElement>(null);
  const [isTelegramScriptLoaded, setIsTelegramScriptLoaded] = useState(false);

  const { showErrorToast } = useToast();

  const [searchParams] = useSearchParams();
  const searchObject = Object.fromEntries(searchParams);
  const { redirectUrl, ...telegramAuth } = searchObject;

  const token = searchParams.has('hash') ? generateBase64(JSON.stringify(telegramAuth)) : undefined;

  const [loginByTelegram, { loading }] = useLoginByTelegramTokenMutation();

  const setupTelegramWidget = () => {
    if (!BOT_NAME) {
      // eslint-disable-next-line no-console
      console.error('Отсутствует BOT_NAME');
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.src = 'https://telegram.org/js/telegram-widget.js?22';
    scriptElement.setAttribute('data-telegram-login', BOT_NAME);
    scriptElement.setAttribute('data-size', 'large');
    scriptElement.setAttribute('data-userpic', 'false');
    scriptElement.setAttribute('data-auth-url', window.location.href);
    scriptElement.setAttribute('data-request-access', 'write');

    scriptElement.async = true;
    scriptElement.onload = () => setIsTelegramScriptLoaded(true);

    telegramWrapperRef.current?.appendChild(scriptElement);
  };

  useEffect(() => {
    setLoginLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!me && !token) {
      setupTelegramWidget();
    }
  }, [meLoading, me]);

  useEffect(() => {
    if (token && !meLoading && !me) {
      loginByTelegram({
        variables: {
          token,
        },
      })
        .then((res) => {
          const loginByTelegramToken = res.data?.loginByTelegramToken;
          const accessToken = loginByTelegramToken?.accessToken;
          const refreshToken = loginByTelegramToken?.refreshToken;

          handleLogin(accessToken, refreshToken);
        })
        .catch(() => showErrorToast(t('errors.common')));
    }
  }, [token, me, meLoading]);

  if (loading || meLoading) return <CircularLoading />;

  return (
    <Box
      component='div'
      sx={{
        width: 'fit-content',
        display: isTelegramScriptLoaded ? 'flex' : 'none',
        borderRadius: 5,
        overflow: 'hidden',
      }}
      ref={telegramWrapperRef}
    />
  );
};

export default TelegramLoginWidget;
